import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Trash2, IndianRupee, User, Phone, ShoppingBag, CreditCard, Banknote, Bike, UtensilsCrossed, CheckCircle, QrCode, X, Printer, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import { format } from 'date-fns';
import QRCode from 'qrcode';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  customer_name: '',
  customer_phone: '',
  order_type: 'dine_in',
  payment_method: 'cash',
  items: [{ menu_item_id: 'manual', name: '', price: '', quantity: 1 }],
};

const defaultProfile = {
  restaurant_name: '', address: '', phone: '', gstin: '', fssai: '', logo_url: '',
  gst_enabled: false, gst_rate: 5, gst_inclusive: false,
  service_charge_enabled: false, service_charge_rate: 5,
  packaging_charge_enabled: false, packaging_charge_amount: 20,
  delivery_charge_enabled: false, delivery_charge_amount: 40,
  default_discount_enabled: false, default_discount_type: 'percent', default_discount_value: 0,
};

const Billing = () => {
  const { restaurantId } = useAuth();
  const [records, setRecords] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const location = useLocation();
  const [upiSettings, setUpiSettings] = useState({ upi_id: '', restaurant_name: '' });
  const [profile, setProfile] = useState(defaultProfile);
  const [charges, setCharges] = useState({ gst: true, service: true, packaging: true, delivery: true });

  // Discount state
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'flat'
  const [discountValue, setDiscountValue] = useState('');
  const [discountEnabled, setDiscountEnabled] = useState(false);

  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiQrDataUrl, setUpiQrDataUrl] = useState('');

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (location.state?.prefill) {
      const { items, table_number } = location.state.prefill;
      setForm(prev => ({
        ...prev,
        customer_name: `Table ${table_number}`,
        items: items.map(item => ({
          menu_item_id: item.menu_item_id || 'manual',
          name: item.name,
          price: item.price.toString(),
          quantity: item.quantity,
        })),
      }));
      setShowForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchAll = async () => {
    try {
      const [billingRes, menuRes] = await Promise.all([api.getBillingRecords(), api.getMenu(restaurantId)]);
      setRecords(billingRes.data);
      setMenuItems(menuRes.data);
    } catch { toast.error('Failed to load billing data'); }
    finally { setLoading(false); }
    try {
      const upiRes = await api.getUpiSettings();
      setUpiSettings(upiRes.data || { upi_id: '', restaurant_name: '' });
    } catch {}
    try {
      const profileRes = await api.getRestaurantProfile();
      const p = { ...defaultProfile, ...profileRes.data };
      setProfile(p);
      // Apply default discount from settings
      if (p.default_discount_enabled) {
        setDiscountEnabled(true);
        setDiscountType(p.default_discount_type || 'percent');
        setDiscountValue(p.default_discount_value?.toString() || '');
      }
    } catch {}
  };

  const subtotal = form.items.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0), 0
  );

  // Discount applied BEFORE GST
  const getDiscountAmount = (sub) => {
    if (!discountEnabled || !discountValue || parseFloat(discountValue) <= 0) return 0;
    if (discountType === 'percent') {
      const pct = Math.min(parseFloat(discountValue) || 0, 100);
      return parseFloat(((sub * pct) / 100).toFixed(2));
    }
    return Math.min(parseFloat(discountValue) || 0, sub);
  };

  const calcCharges = (sub, orderType) => {
    const discountAmount = getDiscountAmount(sub);
    const afterDiscount = parseFloat((sub - discountAmount).toFixed(2));

    const isDineIn = orderType === 'dine_in';
    const isDelivery = orderType === 'delivery';
    let serviceCharge = 0, packagingCharge = 0, deliveryCharge = 0;
    let cgst = 0, sgst = 0, gstAmount = 0;

    if (profile.service_charge_enabled && charges.service && isDineIn)
      serviceCharge = parseFloat(((afterDiscount * profile.service_charge_rate) / 100).toFixed(2));
    if (profile.packaging_charge_enabled && charges.packaging && isDelivery)
      packagingCharge = parseFloat(profile.packaging_charge_amount || 0);
    if (profile.delivery_charge_enabled && charges.delivery && isDelivery)
      deliveryCharge = parseFloat(profile.delivery_charge_amount || 0);

    const beforeGst = afterDiscount + serviceCharge + packagingCharge + deliveryCharge;

    if (profile.gst_enabled && charges.gst) {
      if (profile.gst_inclusive) {
        gstAmount = parseFloat((beforeGst - (beforeGst / (1 + profile.gst_rate / 100))).toFixed(2));
      } else {
        gstAmount = parseFloat(((beforeGst * profile.gst_rate) / 100).toFixed(2));
      }
      cgst = parseFloat((gstAmount / 2).toFixed(2));
      sgst = parseFloat((gstAmount / 2).toFixed(2));
    }

    const grandTotal = profile.gst_inclusive
      ? beforeGst
      : parseFloat((beforeGst + gstAmount).toFixed(2));

    return { discountAmount, afterDiscount, serviceCharge, packagingCharge, deliveryCharge, cgst, sgst, gstAmount, grandTotal, beforeGst };
  };

  const computed = calcCharges(subtotal, form.order_type);

  const addItem = () => setForm({ ...form, items: [...form.items, { menu_item_id: 'manual', name: '', price: '', quantity: 1 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const updateItem = (idx, field, value) => {
    const updated = form.items.map((item, i) => {
      if (i !== idx) return item;
      if (field === 'menu_item_id' && value !== 'manual') {
        const found = menuItems.find((m) => m.id === value);
        if (found) return { ...item, menu_item_id: value, name: found.name, price: found.price.toString() };
      }
      return { ...item, [field]: value };
    });
    setForm({ ...form, items: updated });
  };

  const getValidItems = () =>
    form.items.filter((i) => i.name.trim() && parseFloat(i.price) > 0 && parseInt(i.quantity) > 0);

  const validateForm = () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return false; }
    if (!form.customer_phone.trim()) { toast.error('Customer phone is required'); return false; }
    if (getValidItems().length === 0) { toast.error('Add at least one valid item'); return false; }
    return true;
  };

  const handleSave = async (paymentStatus = 'paid') => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const c = calcCharges(subtotal, form.order_type);
      const discLabel = discountEnabled && parseFloat(discountValue) > 0
        ? (discountType === 'percent' ? `${discountValue}%` : `₹${discountValue} flat`)
        : null;
      await api.createBillingRecord({
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        order_type: form.order_type,
        payment_method: form.payment_method,
        payment_status: paymentStatus,
        items: getValidItems().map((i) => ({ menu_item_id: i.menu_item_id, name: i.name, price: parseFloat(i.price), quantity: parseInt(i.quantity) })),
        items_subtotal: subtotal,
        discount_amount: c.discountAmount,
        discount_label: discLabel,
        service_charge: c.serviceCharge,
        packaging_charge: c.packagingCharge,
        delivery_charge: c.deliveryCharge,
        cgst: c.cgst,
        sgst: c.sgst,
        subtotal: c.grandTotal,
      });
      toast.success('Bill saved successfully');
      setForm(emptyForm);
      setDiscountEnabled(false);
      setDiscountValue('');
      setShowForm(false);
      setShowUpiModal(false);
      fetchAll();
    } catch { toast.error('Failed to save bill'); }
    finally { setSaving(false); }
  };

  const handleShowUpiQr = async () => {
    if (!validateForm()) return;
    if (subtotal <= 0) { toast.error('Total must be greater than 0'); return; }
    if (!upiSettings.upi_id) { toast.error('UPI ID not set. Please add your UPI ID in Settings first.'); return; }
    const upiLink = `upi://pay?pa=${upiSettings.upi_id}&pn=${encodeURIComponent(upiSettings.restaurant_name || profile.restaurant_name || 'Restaurant')}&am=${computed.grandTotal.toFixed(2)}&cu=INR`;
    try {
      const dataUrl = await QRCode.toDataURL(upiLink, { width: 280, margin: 2, color: { dark: '#111827', light: '#ffffff' } });
      setUpiQrDataUrl(dataUrl);
      setShowUpiModal(true);
    } catch { toast.error('Failed to generate QR code'); }
  };

  const handlePrint = async (record) => {
    const rName = profile.restaurant_name || upiSettings.restaurant_name || 'Restaurant';

    // Convert logo to base64 BEFORE opening print window
    // (external URLs are blocked by browsers in about:blank print windows)
    let logoBase64 = '';
    if (profile.logo_url) {
      try {
        const res = await fetch(profile.logo_url);
        const blob = await res.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        // If fetch fails (e.g. CORS), fall back to direct URL
        logoBase64 = profile.logo_url;
      }
    }

    const printWindow = window.open('', '_blank', 'width=400,height=750');
    if (!printWindow) { toast.error('Popup blocked. Please allow popups for this site to print bills.'); return; }

    const itemsHtml = record.items.map(item =>
      `<tr>
        <td style="padding:3px 0;font-size:12px;">${item.name}</td>
        <td style="padding:3px 0;font-size:12px;text-align:center;">x${item.quantity}</td>
        <td style="padding:3px 0;font-size:12px;text-align:right;">Rs.${(item.price * item.quantity).toFixed(2)}</td>
      </tr>`
    ).join('');

    // Use saved breakdown fields from record
    const itemsSub = (record.items_subtotal > 0 ? record.items_subtotal : record.items.reduce((s, i) => s + (i.price * i.quantity), 0));
    const discAmt  = record.discount_amount || 0;
    const svcAmt   = record.service_charge  || 0;
    const pkgAmt   = record.packaging_charge || 0;
    const dlvAmt   = record.delivery_charge  || 0;
    const cgstAmt  = record.cgst || 0;
    const sgstAmt  = record.sgst || 0;
    const grandTotal = (record.subtotal || 0).toFixed(2);

    const logoHtml = logoBase64
      ? `<div class="center" style="margin-bottom:6px;"><img src="${logoBase64}" alt="logo" style="max-width:80px;max-height:60px;object-fit:contain;" /></div>`
      : '';

    const breakdownHtml = `
      <table>
        <tr class="row"><td>Items Subtotal</td><td style="text-align:right;">Rs.${itemsSub.toFixed(2)}</td></tr>
        ${discAmt > 0 ? `<tr class="row"><td>Discount${record.discount_label ? ' ('+record.discount_label+')' : ''}</td><td style="text-align:right;color:#dc2626;">- Rs.${discAmt.toFixed(2)}</td></tr>` : ''}
        ${svcAmt > 0 ? `<tr class="row"><td>Service Charge</td><td style="text-align:right;">Rs.${svcAmt.toFixed(2)}</td></tr>` : ''}
        ${pkgAmt > 0 ? `<tr class="row"><td>Packaging</td><td style="text-align:right;">Rs.${pkgAmt.toFixed(2)}</td></tr>` : ''}
        ${dlvAmt > 0 ? `<tr class="row"><td>Delivery</td><td style="text-align:right;">Rs.${dlvAmt.toFixed(2)}</td></tr>` : ''}
        ${cgstAmt > 0 ? `<tr class="row"><td>CGST</td><td style="text-align:right;">Rs.${cgstAmt.toFixed(2)}</td></tr>` : ''}
        ${sgstAmt > 0 ? `<tr class="row"><td>SGST</td><td style="text-align:right;">Rs.${sgstAmt.toFixed(2)}</td></tr>` : ''}
      </table>
    `;

    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Bill</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'Courier New',monospace; width:80mm; padding:8px; font-size:12px; }
        .center { text-align:center; }
        .bold { font-weight:bold; }
        .divider { border-top:1px dashed #000; margin:5px 0; }
        table { width:100%; border-collapse:collapse; }
        .row td { padding:2px 0; font-size:11px; }
        .grand td { font-weight:bold; font-size:14px; padding-top:4px; border-top:2px solid #000; }
        .footer { text-align:center; font-size:10px; margin-top:6px; }
      </style></head><body>
        ${logoHtml}
        <div class="center bold" style="font-size:15px;">${rName}</div>
        ${profile.address ? `<div class="center" style="font-size:10px;">${profile.address}</div>` : ''}
        ${profile.phone ? `<div class="center" style="font-size:10px;">Ph: ${profile.phone}</div>` : ''}
        <div class="divider"></div>
        <div style="font-size:11px;">Customer: ${record.customer_name}</div>
        <div style="font-size:11px;">Phone: ${record.customer_phone}</div>
        <div style="font-size:11px;">Type: ${record.order_type === 'dine_in' ? 'Dine In' : 'Delivery'}</div>
        <div style="font-size:11px;">Date: ${new Date(record.created_at).toLocaleString('en-IN')}</div>
        <div class="divider"></div>
        <table>
          <thead><tr>
            <th style="text-align:left;font-size:11px;padding-bottom:3px;">Item</th>
            <th style="text-align:center;font-size:11px;">Qty</th>
            <th style="text-align:right;font-size:11px;">Amt</th>
          </tr></thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <div class="divider"></div>
        ${breakdownHtml}
        <div class="divider"></div>
        <table>
          <tr class="grand">
            <td>GRAND TOTAL</td>
            <td style="text-align:right;">Rs.${grandTotal}</td>
          </tr>
        </table>
        <div class="divider"></div>
        <div style="font-size:11px;">Payment: ${record.payment_method === 'cash' ? 'Cash' : record.payment_method === 'upi' ? 'UPI / QR' : 'Card'}</div>
        <div style="font-size:11px;">Status: <b>${record.payment_status === 'paid' ? 'PAID' : 'PENDING'}</b></div>
        ${profile.gstin ? `<div class="divider"></div><div style="font-size:10px;">GSTIN: ${profile.gstin}</div>` : ''}
        ${profile.fssai ? `<div style="font-size:10px;">FSSAI: ${profile.fssai}</div>` : ''}
        <div class="divider"></div>
        <div class="footer">** Thank you, visit again! **</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this billing record?')) return;
    try { await api.deleteBillingRecord(id); toast.success('Record deleted'); setRecords(records.filter((r) => r.id !== id)); }
    catch { toast.error('Failed to delete record'); }
  };

  const totalRevenue = records.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (r.subtotal || 0), 0);
  const cashTotal = records.filter((r) => r.payment_method === 'cash').reduce((sum, r) => sum + (r.subtotal || 0), 0);
  const upiTotal = records.filter((r) => r.payment_method === 'upi').reduce((sum, r) => sum + (r.subtotal || 0), 0);

  const ic = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100]';

  if (loading) return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto animate-pulse">
        <div className="h-8 w-40 bg-gray-200 rounded-lg mb-2" />
        <div className="h-4 w-56 bg-gray-100 rounded mb-6" />
        {/* Stats row skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
              <div className="h-8 w-16 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
        {/* Form + table layout skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-10 bg-gray-100 rounded-lg" />
            ))}
            <div className="h-10 w-full bg-gray-200 rounded-lg mt-2" />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="h-12 bg-gray-50 border-b border-gray-200" />
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-4 px-5 py-4 border-b border-gray-100">
                <div className="h-5 w-24 bg-gray-200 rounded" />
                <div className="h-5 w-16 bg-gray-100 rounded" />
                <div className="h-5 w-20 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Billing</h2>
            <p className="text-gray-600 mt-1">Create bills and collect payments</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#E65100] hover:bg-[#D84800] text-white">
            <Plus className="w-5 h-5 mr-2" />New Bill
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-1"><div className="p-2 bg-orange-100 rounded-lg"><IndianRupee className="w-5 h-5 text-[#E65100]" /></div><span className="text-sm font-medium text-gray-600">Total Revenue</span></div>
            <p className="text-3xl font-bold text-gray-900">₹{totalRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-1"><div className="p-2 bg-green-100 rounded-lg"><Banknote className="w-5 h-5 text-green-600" /></div><span className="text-sm font-medium text-gray-600">Cash Collected</span></div>
            <p className="text-3xl font-bold text-gray-900">₹{cashTotal.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-1"><div className="p-2 bg-purple-100 rounded-lg"><QrCode className="w-5 h-5 text-purple-600" /></div><span className="text-sm font-medium text-gray-600">UPI Collected</span></div>
            <p className="text-3xl font-bold text-gray-900">₹{upiTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* New Bill Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-5">New Bill</h3>

            {/* Customer info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"><User className="w-4 h-4 inline mr-1" />Customer Name *</label>
                <input type="text" placeholder="Enter customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={ic} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"><Phone className="w-4 h-4 inline mr-1" />Contact Number *</label>
                <input type="tel" placeholder="Enter phone number" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={ic} />
              </div>
            </div>

            {/* Order type */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
              <div className="flex gap-3">
                {[['dine_in', 'Dine In', UtensilsCrossed], ['delivery', 'Delivery', Bike]].map(([val, label, Icon]) => (
                  <button key={val} onClick={() => setForm({ ...form, order_type: val })} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 font-medium text-sm transition ${form.order_type === val ? 'border-[#E65100] bg-orange-50 text-[#E65100]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Items */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700"><ShoppingBag className="w-4 h-4 inline mr-1" />Items</label>
                <button onClick={addItem} className="text-sm text-[#E65100] hover:underline font-medium">+ Add Item</button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <select value={item.menu_item_id} onChange={(e) => updateItem(idx, 'menu_item_id', e.target.value)} className={ic}>
                        <option value="manual">— Type manually —</option>
                        {menuItems.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3"><input type="text" placeholder="Item name" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} className={ic} /></div>
                    <div className="col-span-2">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                        <input type="number" placeholder="Price" value={item.price} onChange={(e) => updateItem(idx, 'price', e.target.value)} className="w-full border border-gray-300 rounded-lg pl-6 pr-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100]" />
                      </div>
                    </div>
                    <div className="col-span-1"><input type="number" min="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className={ic} /></div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              {/* Subtotal row */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-base font-medium text-gray-700">Items Subtotal: <span className="text-gray-900 font-semibold">₹{subtotal.toFixed(2)}</span></div>
                <Button variant="outline" onClick={() => { setShowForm(false); setForm(emptyForm); setDiscountEnabled(false); setDiscountValue(''); }}>Cancel</Button>
              </div>

              {/* ── DISCOUNT SECTION ── */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">Discount</span>
                    <span className="text-xs text-green-600">(applied before GST)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setDiscountEnabled(!discountEnabled); if (discountEnabled) setDiscountValue(''); }}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${discountEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${discountEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
                {discountEnabled && (
                  <div className="flex gap-2 items-center mt-2">
                    <div className="flex rounded-lg border border-green-300 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setDiscountType('percent')}
                        className={`px-3 py-1.5 text-sm font-medium transition ${discountType === 'percent' ? 'bg-green-500 text-white' : 'bg-white text-gray-600'}`}
                      >% Off</button>
                      <button
                        type="button"
                        onClick={() => setDiscountType('flat')}
                        className={`px-3 py-1.5 text-sm font-medium transition ${discountType === 'flat' ? 'bg-green-500 text-white' : 'bg-white text-gray-600'}`}
                      >₹ Flat</button>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={discountType === 'percent' ? 100 : subtotal}
                      placeholder={discountType === 'percent' ? 'e.g. 10' : 'e.g. 50'}
                      value={discountValue}
                      onChange={e => setDiscountValue(e.target.value)}
                      className="flex-1 border border-green-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                    {computed.discountAmount > 0 && (
                      <span className="text-sm font-bold text-green-700 whitespace-nowrap">- ₹{computed.discountAmount.toFixed(2)}</span>
                    )}
                  </div>
                )}
              </div>

              {/* ── CHARGES BREAKDOWN ── */}
              <div className="bg-gray-50 rounded-xl p-3 mb-3 space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bill Breakdown</p>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Items Subtotal</span><span>₹{subtotal.toFixed(2)}</span>
                </div>
                {discountEnabled && computed.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600 font-medium">
                    <span>Discount {discountType === 'percent' ? `(${discountValue}%)` : '(Flat)'}</span>
                    <span>- ₹{computed.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {profile.service_charge_enabled && form.order_type === 'dine_in' && (
                  <div className="flex justify-between text-sm items-center">
                    <button onClick={() => setCharges(c => ({...c, service: !c.service}))} className={`font-medium ${charges.service ? 'text-blue-600' : 'text-gray-400 line-through'}`}>
                      Service Charge {profile.service_charge_rate}%
                    </button>
                    <span className={charges.service ? 'text-gray-900' : 'text-gray-400'}>₹{computed.serviceCharge.toFixed(2)}</span>
                  </div>
                )}
                {profile.packaging_charge_enabled && form.order_type === 'delivery' && (
                  <div className="flex justify-between text-sm items-center">
                    <button onClick={() => setCharges(c => ({...c, packaging: !c.packaging}))} className={`font-medium ${charges.packaging ? 'text-blue-600' : 'text-gray-400 line-through'}`}>
                      Packaging Charge
                    </button>
                    <span className={charges.packaging ? 'text-gray-900' : 'text-gray-400'}>₹{computed.packagingCharge.toFixed(2)}</span>
                  </div>
                )}
                {profile.delivery_charge_enabled && form.order_type === 'delivery' && (
                  <div className="flex justify-between text-sm items-center">
                    <button onClick={() => setCharges(c => ({...c, delivery: !c.delivery}))} className={`font-medium ${charges.delivery ? 'text-blue-600' : 'text-gray-400 line-through'}`}>
                      Delivery Charge
                    </button>
                    <span className={charges.delivery ? 'text-gray-900' : 'text-gray-400'}>₹{computed.deliveryCharge.toFixed(2)}</span>
                  </div>
                )}
                {profile.gst_enabled && (
                  <div className="flex justify-between text-sm items-center">
                    <button onClick={() => setCharges(c => ({...c, gst: !c.gst}))} className={`font-medium ${charges.gst ? 'text-blue-600' : 'text-gray-400 line-through'}`}>
                      CGST + SGST ({profile.gst_rate}%) {profile.gst_inclusive ? '(incl.)' : ''}
                    </button>
                    <span className={charges.gst ? 'text-gray-900' : 'text-gray-400'}>₹{(computed.cgst + computed.sgst).toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-300 pt-2 flex justify-between font-bold text-base">
                  <span>Grand Total</span>
                  <span className="text-[#E65100]">₹{computed.grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment method */}
              <p className="text-sm font-medium text-gray-700 mb-3">Select payment method:</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  ['cash', 'Cash', Banknote, 'bg-green-50 border-green-500 text-green-700'],
                  ['card', 'Card', CreditCard, 'bg-blue-50 border-blue-500 text-blue-700'],
                  ['upi', 'UPI / QR', QrCode, 'bg-purple-50 border-purple-500 text-purple-700'],
                ].map(([val, label, Icon, activeClass]) => (
                  <button key={val} onClick={() => setForm({ ...form, payment_method: val })}
                    className={`flex flex-col items-center gap-1 py-3 px-4 rounded-xl border-2 font-medium text-sm transition ${form.payment_method === val ? activeClass : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    <Icon className="w-5 h-5" /><span>{label}</span>
                  </button>
                ))}
              </div>

              {form.payment_method === 'upi' ? (
                <Button onClick={handleShowUpiQr} className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-base font-semibold">
                  <QrCode className="w-5 h-5 mr-2" /> Show UPI QR — ₹{computed.grandTotal.toFixed(2)}
                </Button>
              ) : (
                <Button onClick={() => handleSave('paid')} disabled={saving} className="w-full bg-gray-800 hover:bg-gray-900 text-white h-12 text-base font-semibold">
                  {saving ? 'Saving...' : `Save Bill — ₹${computed.grandTotal.toFixed(2)}`}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* UPI QR Modal */}
        {showUpiModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Scan to Pay</h3>
                <button onClick={() => setShowUpiModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="text-center mb-4">
                <p className="text-3xl font-bold text-purple-600 mb-1">₹{computed.grandTotal.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Amount is pre-filled in UPI app</p>
              </div>
              {upiQrDataUrl && (
                <div className="flex justify-center mb-4">
                  <div className="border-2 border-purple-200 rounded-xl p-3 bg-white">
                    <img src={upiQrDataUrl} alt="UPI QR Code" className="w-56 h-56" />
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center">
                <p className="text-xs text-gray-500 mb-1">UPI ID</p>
                <p className="text-sm font-mono font-semibold text-gray-800">{upiSettings.upi_id}</p>
                {upiSettings.restaurant_name && <p className="text-xs text-gray-500 mt-1">{upiSettings.restaurant_name}</p>}
              </div>
              <p className="text-xs text-center text-gray-400 mb-4">Works with GPay, PhonePe, Paytm and all UPI apps</p>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => setShowUpiModal(false)} className="h-11">Cancel</Button>
                <Button onClick={() => handleSave('paid')} disabled={saving} className="h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold">
                  {saving ? 'Saving...' : 'Mark as Paid'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Records Table */}
        {records.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <IndianRupee className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No billing records yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Customer', 'Items', 'Total', 'Type', 'Payment', 'Status', 'Date & Time', ''].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4"><p className="font-semibold text-gray-900">{record.customer_name}</p><p className="text-sm text-gray-500">{record.customer_phone}</p></td>
                      <td className="px-5 py-4"><div className="text-sm text-gray-700 space-y-0.5">{record.items.map((item, idx) => <div key={idx}>{item.name} × {item.quantity}</div>)}</div></td>
                      <td className="px-5 py-4"><span className="text-lg font-bold text-[#E65100]">₹{(record.subtotal || 0).toFixed(2)}</span></td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${record.order_type === 'dine_in' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {record.order_type === 'dine_in' ? <UtensilsCrossed className="w-3 h-3" /> : <Bike className="w-3 h-3" />}
                          {record.order_type === 'dine_in' ? 'Dine In' : 'Delivery'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${record.payment_method === 'cash' ? 'bg-green-100 text-green-700' : record.payment_method === 'upi' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {record.payment_method === 'cash' ? <Banknote className="w-3 h-3" /> : record.payment_method === 'upi' ? <QrCode className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                          {record.payment_method === 'cash' ? 'Cash' : record.payment_method === 'upi' ? 'UPI' : 'Card'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {record.payment_status === 'paid'
                          ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" /> Paid</span>
                          : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Pending</span>}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        <div>{format(new Date(record.created_at), 'dd MMM yyyy')}</div>
                        <div className="text-gray-400">{format(new Date(record.created_at), 'hh:mm a')}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handlePrint(record)} className="text-blue-400 hover:text-blue-600 transition" title="Print bill"><Printer className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(record.id)} className="text-red-400 hover:text-red-600 transition" title="Delete record"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
